import { of as observableOf } from 'rxjs';

import { switchMap, map, withLatestFrom, filter, catchError, tap } from 'rxjs/operators';
import { Injectable, Inject, Optional, PLATFORM_ID } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import { Headers } from '@angular/http';
import {Store, select} from '@ngrx/store';
import {ActivatedRoute, Params} from '@angular/router';
import {Http} from "@angular/http";
import {isPlatformBrowser} from '@angular/common'
import {normalize, schema} from 'normalizr';
import { REQUEST } from '@nguniversal/express-engine/tokens';

import {
  SELECT_ITEM,
  REQUEST_ITEM,
  INVALIDATE_SHARE,
  UNLOCK_SHARE,
  COPY_SHARE,
  MOVE_NOTE,
  NETWORK_ERROR,
  WRONG_PASSWORD,
  recieveShare,
  SET_SHARE_ACCESS,
  updateShare,
  DELETE_SHARE,
  SET_SHARE_TITLE,
  updateNote,
  NOTE_MOVE_FAILED, recieveCopyShareResult,
} from "./share.actions";

import { IAppState } from '../app.state';

import { Config } from '../universal-config';
import { selectShare } from './models';
import { setIsShareAccessUpdating, setIsSharePasswordUpdating, showToast } from '../visual/visual.actions';

const text = new schema.Entity('texts', {}, {idAttribute: 'noteGlobalId'});
const attachment = new schema.Entity('attachments', {}, {idAttribute: 'globalId'});
const note = new schema.Entity('notes', {},  {idAttribute: 'globalId'});
const todo = new schema.Entity('todos', {}, {idAttribute: 'globalId'});
note.define({
  text,
  attachments: new schema.Array(attachment),
  todos: new schema.Array(todo),
  children: new schema.Array(note)
});
const share = new schema.Entity('shares', {note, parentNode: note});

@Injectable()
export class ShareEffects {
  url = '/shares';
  shareApiService;

  @Effect() invalidtateShare$ = this.actions$.ofType(INVALIDATE_SHARE).pipe(
    map(() => {
      return {type: REQUEST_ITEM};
    })
  );

  @Effect() selectShare$ = this.actions$.ofType(SELECT_ITEM).pipe(
    map(() => {
      return {type: REQUEST_ITEM};
    })
  );

  @Effect() requestShare$ = this.actions$.ofType(REQUEST_ITEM).pipe(
    withLatestFrom(this.store$.pipe(select(selectShare))),
    map(([ action, share ]) => (share)),
    filter((share:any) => (share.isInvalidated)),
    map((share:any) => {
      const currentShare = share.share;
      return {
        id: currentShare.id,
        securityKey: currentShare.securityKey,
        nodeId: share.nodeId
      };
    }),
    switchMap((selectedShare) => {
      let requestString = `${this.shareApiService}${this.url}` +
        `/${selectedShare.id}?securityKey=${selectedShare.securityKey}`;
      if (selectedShare.nodeId) {
        requestString += `&nodeId=${selectedShare.nodeId}`;
      }
      if (this.request && this.request.query) {
        const query = this.request.query
        requestString += '&' + Object.keys(query).map(key => `${key}=${query[key]}`).join('&');
      }
      return this.http.get(requestString);
    }),
    map((response) => {
      const result = prepareNotes(normalize(response.json(), share));
      const isFetchedOnClient = isPlatformBrowser(this.platformId);
      return recieveShare(result, isFetchedOnClient);
    }),
    catchError((response) => {
      return observableOf({type: NETWORK_ERROR, code: response.status});
    }),
  );

  @Effect() copyShare$ = this.actions$
    .ofType(COPY_SHARE).pipe(
      withLatestFrom(this.store$),
      filter(([action, state]) => (!state.share.shareCopyJobId)),
      switchMap(([action, state]) => {
        const workspaceId = state.workspaces.selectedWorkspaceId;
        const currentShare = state.share.share;
        let requestString = `${this.shareApiService}${this.url}` +
          `/${currentShare.id}?securityKey=${currentShare.securityKey}`;
        if (state.share.nodeId) {
          requestString += `&nodeId=${state.share.nodeId}`;
        }
        if(workspaceId) {
          requestString += `&workspaceId=${workspaceId}`
        }
        return this.http.post(requestString, {});
      }),
      map((response) => {
        const result = response.json();
        return recieveCopyShareResult(result);
      }),
      catchError((err) => {
        return observableOf({type: 'REQUEST_FAILED'});
      }),
    );

  @Effect() moveNote$ = this.actions$
    .ofType(MOVE_NOTE).pipe(
      withLatestFrom(this.store$),
      filter(([action, state]) => (!state.share.shareCopyJobId)),
      switchMap(([action, state]) => {
        const share = state.share.share;
        const shareId = share.id;
        const securityKey = share.securityKey;
        const { noteId, currentWorkspaceId, workspaceIdToMove } = action as any;
        let requestString = `${this.shareApiService}${this.url}` +
          `/${shareId}/move?securityKey=${securityKey}`;
        return this.http.post(requestString, {workspaceIdToMove, workspaceId: currentWorkspaceId, noteId});
      }),
      map((response) => {
        const result = prepareNotes(normalize(response.json(), share));
        const isFetchedOnClient = isPlatformBrowser(this.platformId);
        this.store$.dispatch(showToast({
          id: 1,
          text: 'The page is successfully moved',
          position: 'right',
        }));
        return recieveShare(result, isFetchedOnClient);
      }),
      catchError((err) => {
        return observableOf({type: 'NOTE_MOVE_FAILED'});
      }),
    );


  @Effect() setShareAccess$ = this.actions$
    .ofType(SET_SHARE_ACCESS).pipe(
      withLatestFrom(this.store$),
      filter(([action, state]) => (!state.visual.isShareAccessUpdating)),
      switchMap(([action, state]) => {
        const share = state.share.share;
        const noteId = share.noteGlobalId;
        const { accessControl, password } = (action as any).data;
        const workspaceId = share.workspaceId;

        let requestString = `${this.shareApiService}${this.url}` +
          `/${noteId}/access?workspaceId=${workspaceId}`;

        if(accessControl) {
          this.store$.dispatch(setIsShareAccessUpdating(true));
          requestString += `&accessControl=${accessControl}`
          return this.http.post(requestString, {accessControl});
        } else {
          this.store$.dispatch(setIsSharePasswordUpdating(true));
          return this.http.post(requestString, {password});
        }
      }),
      map((response) => {
        const result = response.json();
        this.store$.dispatch(setIsShareAccessUpdating(false));
        this.store$.dispatch(setIsSharePasswordUpdating(false));
        result.accessAllowed = true;
        return updateShare(result);
      }),
      catchError((err) => {
        this.store$.dispatch(setIsShareAccessUpdating(false));
        this.store$.dispatch(setIsSharePasswordUpdating(false));
        return observableOf({type: 'REQUEST_FAILED'});
      }),
    );


  @Effect() deteleShare$ = this.actions$
    .ofType(DELETE_SHARE).pipe(
      withLatestFrom(this.store$),
      filter(([action, state]) => (!state.share.shareCopyJobId)),
      switchMap(([action, state]) => {
        const share = state.share.share;
        const noteId = share.noteGlobalId;
        const workspaceId = share.workspaceId;

        let requestString = `${this.shareApiService}${this.url}` +
          `/${noteId}/?workspaceId=${workspaceId}`;

        return this.http.delete(requestString);
      }),
      map((response) => {
        const result = response.json();
        location.href=location.href;
        return updateShare(result);
      }),
      catchError((err) => {
        return observableOf({type: 'REQUEST_FAILED'});
      }),
    );

  @Effect() setShareTitle$ = this.actions$
    .ofType(SET_SHARE_TITLE).pipe(
      withLatestFrom(this.store$),
      filter(([action, state]) => (!state.share.shareCopyJobId)),
      switchMap(([action, state]) => {
        const share = state.share.share;
        const noteId = share.noteGlobalId;
        const workspaceId = share.workspaceId;
        const title = (action as any).title;

        let requestString = `${this.shareApiService}${this.url}` +
          `/${noteId}/title?workspaceId=${workspaceId}`;

        return this.http.post(requestString, {title});
      }),
      map((response) => {
        const result = response.json();
        return updateNote(result.globalId, {title: result.title});
      }),
      catchError((err) => {
        return observableOf({type: 'REQUEST_FAILED'});
      }),
    );

  @Effect() unlockShare$ = this.actions$
    .ofType(UNLOCK_SHARE).pipe(
      withLatestFrom(this.store$),
      switchMap(([action, state]) => {
          const typeAction = action as any;
          const currentShare = state.share.share;
          let requestString = `${this.shareApiService}${this.url}` +
            `/${currentShare.id}/auth?securityKey=${currentShare.securityKey}`;
          if (state.share.nodeId) {
            requestString += `&nodeId=${state.share.nodeId}`;
          }
          const body = JSON.stringify({
            password: typeAction.password
          });
          return this.http.post(requestString, body, {
            headers: new Headers(
              {'Content-Type': 'application/json; charset=utf-8'}
            )
          }).pipe(catchError((response) => {
            if (response.status && response.status == 403) {
              return observableOf(false);
            }
          }));
        }
      ),
      map((response) => {
        if (!response) {
          return {type: WRONG_PASSWORD};
        }
        return {type: REQUEST_ITEM};
      }),
      catchError((response) => {
        return observableOf({ type: NETWORK_ERROR, code: response.status });
      })
    );

  constructor(
    public actions$: Actions,
    public store$: Store<IAppState>,
    public route: ActivatedRoute,
    public http: Http,
    public _config: Config,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(REQUEST) public request: any,
  ) {
    this.shareApiService = _config.get('SHARE_API_SERVICE');
  }
}

function prepareNotes(result) {
  if (!result || !result.entities || !result.entities.shares || !result.result
    || !result.entities.notes) {
      return result;
    }
  const {id, securityKey} = result.entities.shares[result.result];
  Object.keys(result.entities.notes).forEach((noteId) => {
    const note = result.entities.notes[noteId];

    note.nnShareUrl = `/share/${id}/${securityKey}/${note.globalId}`;
  });
  return result;
}

